import { z } from "zod/v3";
import {
  AUTH_FRONT_FAILURES,
  CALLBACK_QUERY,
  CallbackFailure,
  CallbackOutcomeKind,
  type CallbackFailureValue,
} from "./constants";

export type CallbackOutcome =
  | { kind: typeof CallbackOutcomeKind.OK }
  | { kind: typeof CallbackOutcomeKind.ERROR; failure: CallbackFailureValue }
  | { kind: typeof CallbackOutcomeKind.INVALID };

export type SessionTokens = {
  accessToken: string;
  idToken: string;
  expiresIn?: number;
};

const sessionTokensSchema = z.object({
  access_token: z.string().min(1),
  id_token: z.string().min(1),
  expires_in: z
    .number()
    .positive()
    .optional()
    .catch(undefined),
});

function toCallbackFailure(authError: string): CallbackFailureValue {
  return (
    AUTH_FRONT_FAILURES.find((failure) => failure === authError) ??
    CallbackFailure.UNKNOWN
  );
}

export function readCallbackOutcome(search: string): CallbackOutcome {
  const params = new URLSearchParams(search);
  const authError = params.get(CALLBACK_QUERY.AUTH_ERROR);
  const hasAuthError = authError !== null;
  const isAuthOk = params.get(CALLBACK_QUERY.AUTH) === CALLBACK_QUERY.OK;

  if (hasAuthError) {
    return {
      kind: CallbackOutcomeKind.ERROR,
      failure: toCallbackFailure(authError),
    };
  }

  if (isAuthOk) {
    return { kind: CallbackOutcomeKind.OK };
  }

  return { kind: CallbackOutcomeKind.INVALID };
}

export function parseSessionTokens(data: unknown): SessionTokens | null {
  const parsed = sessionTokensSchema.safeParse(data);

  if (!parsed.success) {
    return null;
  }

  return {
    accessToken: parsed.data.access_token,
    idToken: parsed.data.id_token,
    expiresIn: parsed.data.expires_in,
  };
}
